// Textele REZULTATELOR pașilor, în limba ecranului.
//
// Pașii structurii scriu în bază, în română, ce compun la calcul: numele
// verificărilor emise, punctul, nota, referința, motivul. Rezultatul nu se
// traduce la scriere — după numele verificărilor se potrivesc categoriile
// (`reguli_tip`), rândurile P17 cu ale pașilor, iar un job n-are limbă —, deci
// se traduce LA AFIȘARE, aici: runtime-ul JSX al ecranelor de structură
// (`simboluri-jsx`) trece orice text al unui element DOM prin
// `traduceRezultat`, înainte de adnotarea simbolurilor.
//
// Dicționarul vine de la server (`structure/i18n/rezultate.<limba>.json`),
// iar regula de potrivire e aceeași cu a lui `structure/i18n/texte.py::
// Sabloane` — implementarea de acolo e referința, iar `texteRezultat.test.ts`
// cere același text pe eșantionul `structure/tests/data/texte_rezultat.json`:
//
//   * un text fix se caută ca atare;
//   * un ȘABLON are locuri goale, `{1}`, `{element}`: locul prinde cel mai
//     scurt șir care lasă restul să se potrivească (niciodată peste « · »),
//     valoarea lui se traduce la rândul ei, iar o cifră cu virgulă zecimală
//     trece pe punct;
//   * un text fără potrivire întreagă, legat cu « · », se traduce pe bucăți.
//
// Pe română nu se face nimic: nu e niciun dicționar activ.

import { useEffect, useState } from "react";
import { LIMBA_IMPLICITA, useLimba } from "../i18n";
import { getCatalogRezultate } from "./apiStructura";

export type DictionarRezultate = {
  limba: string;
  texte: Record<string, string>;
};

const SLOT = /\{([A-Za-z0-9_]+)\}/g;
const ARE_SLOT = /\{[A-Za-z0-9_]+\}/;
const ZECIMAL_RO = /^[−-]?[0-9]+,[0-9]+$/;
const LITERA = /\p{L}/u;
// ce prinde un loc gol: cel mai scurt șir care lasă restul să se potrivească,
// dar niciodată peste un « · » — bucățile legate așa se traduc fiecare a ei
const LOC_GOL = "((?:(?! · ).)+?)";
const ADANCIME = 3;

/** Un șir are ce traduce dacă are măcar o literă. */
export function deTradus(text: unknown): text is string {
  return typeof text === "string" && LITERA.test(text);
}

function literePeFix(sablon: string): number {
  let n = 0;
  for (const c of sablon.replace(SLOT, "")) if (LITERA.test(c)) n++;
  return n;
}

const escape = (s: string) => s.replace(/[.*+?^${}()|[\]\\/]/g, "\\$&");

type Sablon = {
  cheie: string;
  regex: RegExp;
  sloturi: string[];
  tinta: string;
  // cea mai lungă bucată fixă: un text care n-o conține nu se mai încearcă
  ancora: string;
  litere: number;
};

function compileaza(cheie: string, tinta: string): Sablon {
  const bucati: string[] = [];
  const fixe: string[] = [];
  const sloturi: string[] = [];
  let poz = 0;
  for (const m of cheie.matchAll(SLOT)) {
    fixe.push(cheie.slice(poz, m.index));
    bucati.push(escape(cheie.slice(poz, m.index)), LOC_GOL);
    sloturi.push(m[1]);
    poz = m.index! + m[0].length;
  }
  fixe.push(cheie.slice(poz));
  bucati.push(escape(cheie.slice(poz)));
  return {
    cheie,
    regex: new RegExp(`^${bucati.join("")}$`, "su"),
    sloturi,
    tinta,
    ancora: fixe.reduce((a, b) => (b.length > a.length ? b : a), ""),
    litere: literePeFix(cheie),
  };
}

export class TraducatorRezultate {
  private fixe = new Map<string, string>();
  private sabloane: Sablon[] = [];
  private memo = new Map<string, string>();

  constructor(texte: Record<string, string>) {
    for (const [k, v] of Object.entries(texte)) {
      if (ARE_SLOT.test(k)) this.sabloane.push(compileaza(k, v));
      else this.fixe.set(k, v);
    }
    this.sabloane.sort(
      (a, b) => b.litere - a.litere || (a.cheie < b.cheie ? -1 : a.cheie > b.cheie ? 1 : 0)
    );
  }

  traduce(text: string): string {
    if (!deTradus(text)) return text;
    const gasit = this.memo.get(text);
    if (gasit !== undefined) return gasit;
    const tradus = this.caut(text, 0);
    // tabelele mari scriu multe texte distincte (cifre, id-uri): memoria se
    // golește din când în când, nu crește cât ține sesiunea
    if (this.memo.size > 50_000) this.memo.clear();
    this.memo.set(text, tradus);
    return tradus;
  }

  private caut(text: string, adancime: number): string {
    const fix = this.fixe.get(text);
    if (fix !== undefined) return fix;
    for (const s of this.sabloane) {
      if (!text.includes(s.ancora)) continue;
      const m = s.regex.exec(text);
      if (!m) continue;
      const valori = new Map<string, string>();
      s.sloturi.forEach((nume, i) => valori.set(nume, this.slot(m[i + 1], adancime)));
      return s.tinta.replace(SLOT, (tot, nume: string) => valori.get(nume) ?? tot);
    }
    if (text.includes(" · ")) {
      return text
        .split(" · ")
        .map((p) => (deTradus(p) ? this.caut(p, adancime) : p))
        .join(" · ");
    }
    return text;
  }

  private slot(valoare: string, adancime: number): string {
    if (ZECIMAL_RO.test(valoare)) return valoare.replace(",", ".");
    if (adancime < ADANCIME && deTradus(valoare)) return this.caut(valoare, adancime + 1);
    return valoare;
  }
}

// Dicționarul limbii curente, ca variabilă de modul: runtime-ul JSX nu e o
// componentă și nu poate chema un hook. Îl scrie DOAR `useTexteRezultat`.
let activ: TraducatorRezultate | null = null;

/** `text` în limba ecranului; neatins pe română, cât dicționarul n-a sosit
 *  și când nu e un șir (o cifră, `null`). Runtime-ul JSX îl cheamă pe fiecare
 *  text al unui element; direct îl cheamă doar ecranele care pun un text de
 *  pas ÎNĂUNTRUL unei fraze din catalog (`t("…", { motiv })`) — acolo fraza
 *  întreagă n-ar mai avea cum să se potrivească. */
export function traduceRezultat<T>(text: T): T {
  return (activ && typeof text === "string" ? activ.traduce(text) : text) as T;
}

/** Pune dicționarul sosit de la server (sau niciunul, pe română). */
export function seteazaTexteRezultat(d: DictionarRezultate | null) {
  activ = d && Object.keys(d.texte).length ? new TraducatorRezultate(d.texte) : null;
}

/** Aduce dicționarul limbii ecranului și întoarce o versiune care crește la
 *  fiecare sosire: furnizorul structurii o ține în valoarea lui, ca ecranele
 *  să se redeseneze cu textele traduse. */
export function useTexteRezultat(): number {
  const l = useLimba();
  const [versiune, setVersiune] = useState(0);
  useEffect(() => {
    if (l === LIMBA_IMPLICITA) {
      seteazaTexteRezultat(null);
      setVersiune((v) => v + 1);
      return;
    }
    let viu = true;
    getCatalogRezultate()
      .then((d) => {
        if (!viu) return;
        seteazaTexteRezultat(d.limba === l ? d : null);
        setVersiune((v) => v + 1);
      })
      .catch(() => {});
    return () => {
      viu = false;
    };
  }, [l]);
  return versiune;
}
